import {
  cardStyle,
  ghostButtonStyle,
  primaryButtonStyle,
  SectionTitle,
} from "../dashboardStyles";
import { formatCurrency, formatDistance, getPermissionBadgeStyle } from "../dashboardUtils";

function RankingCard({ children, featured = false, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: 16,
        borderRadius: 22,
        border: featured ? "2px solid #f97316" : "1px solid #fed7aa",
        background: featured ? "#fff7ed" : "#ffffff",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

export default function RankingPage({
  distanceRanking,
  loadMarketRankings,
  openMarketPage,
  ranking,
  t,
  visibleMarkets,
}) {
  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div style={{ ...cardStyle, padding: 24 }}>
        <SectionTitle
          eyebrow={t?.("ranking.hero.eyebrow", "Ranking de lojas") || "Ranking de lojas"}
          title={t?.("ranking.hero.title", "Todos os rankings em um lugar") || "Todos os rankings em um lugar"}
          description={
            t?.(
              "ranking.hero.description",
              "Compare lojas por distancia, prontidao para abrir e indice medio de preco sem poluir o mapa inicial."
            ) || "Compare lojas por distancia, prontidao para abrir e indice medio de preco sem poluir o mapa inicial."
          }
          action={
            <button onClick={() => loadMarketRankings(false)} style={primaryButtonStyle}>
              {t?.("ranking.hero.refresh", "Atualizar rankings") || "Atualizar rankings"}
            </button>
          }
        />
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}
      >
        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("ranking.distance.eyebrow", "Distancia") || "Distancia"}
            title={t?.("ranking.distance.title", "Ranking por distancia") || "Ranking por distancia"}
            description={
              t?.(
                "ranking.distance.description",
                "Mostra primeiro as lojas mais perto da localizacao ou endereco ativo."
              ) || "Mostra primeiro as lojas mais perto da localizacao ou endereco ativo."
            }
          />
          <div style={{ display: "grid", gap: 10 }}>
            {distanceRanking.slice(0, 12).map((market, index) => (
              <RankingCard
                key={`distance-${market.market_id}`}
                featured={index === 0}
                onClick={() =>
                  openMarketPage(market, {
                    statusMessage:
                      t?.("status.market.open.distance", "{{market}} aberto pelo ranking de distancia.", {
                        market: market.market,
                      }) || `${market.market} aberto pelo ranking de distancia.`,
                  })
                }
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <strong>
                    #{index + 1} {market.market}
                  </strong>
                  <span style={getPermissionBadgeStyle(index === 0 ? "granted" : "prompt")}>
                    {formatDistance(market.resolvedDistanceKm ?? market.distance_km)}
                  </span>
                </div>
                <div style={{ marginTop: 8, color: "#7c2d12" }}>
                  {market.address ||
                    t?.("ranking.common.address_missing", "Endereco ainda nao informado") ||
                    "Endereco ainda nao informado"}
                </div>
              </RankingCard>
            ))}
            {distanceRanking.length === 0 && (
              <div style={{ color: "#64748b" }}>
                {t?.("ranking.distance.empty", "Ative a localizacao para gerar o ranking por distancia.") ||
                  "Ative a localizacao para gerar o ranking por distancia."}
              </div>
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <SectionTitle
            eyebrow={t?.("ranking.ready.eyebrow", "Lojas prontas") || "Lojas prontas"}
            title={t?.("ranking.ready.title", "Lojas prontas para abrir") || "Lojas prontas para abrir"}
            description={
              t?.(
                "ranking.ready.description",
                "Atalho para abrir lojas ja carregadas no mapa e ver o catalogo mais recente de cada uma."
              ) || "Atalho para abrir lojas ja carregadas no mapa e ver o catalogo mais recente de cada uma."
            }
          />
          <div style={{ display: "grid", gap: 10 }}>
            {visibleMarkets.slice(0, 12).map((market, index) => (
              <RankingCard
                key={`ready-${market.market_id}`}
                featured={market.market_id === visibleMarkets[0]?.market_id}
                onClick={() =>
                  openMarketPage(market, {
                    statusMessage:
                      t?.("status.market.open.ready", "{{market}} aberto pela lista de lojas prontas.", {
                        market: market.market,
                      }) || `${market.market} aberto pela lista de lojas prontas.`,
                  })
                }
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <strong>
                    #{index + 1} {market.market}
                  </strong>
                  <span style={getPermissionBadgeStyle("prompt")}>
                    {formatDistance(market.resolvedDistanceKm)}
                  </span>
                </div>
                <div style={{ marginTop: 8, color: "#7c2d12" }}>
                  {market.category || t?.("ranking.ready.store", "loja") || "loja"}
                  {market.address ? ` | ${market.address}` : ""}
                </div>
              </RankingCard>
            ))}
            {visibleMarkets.length === 0 && (
              <div style={{ color: "#64748b" }}>
                {t?.("ranking.ready.empty", "Carregue o mapa para ver lojas prontas para abrir.") ||
                  "Carregue o mapa para ver lojas prontas para abrir."}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <SectionTitle
          eyebrow={t?.("ranking.price.eyebrow", "Preco") || "Preco"}
          title={t?.("ranking.price.title", "Melhor indice medio de preco") || "Melhor indice medio de preco"}
            description={
              t?.(
                "ranking.price.description",
                "Ranking calculado apenas com lojas dentro do raio ativo, usando precos, historico e volume de atualizacoes."
              ) || "Ranking calculado apenas com lojas dentro do raio ativo, usando precos, historico e volume de atualizacoes."
            }
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          {ranking.slice(0, 12).map((item, index) => (
            <RankingCard
              key={`price-${item.market_id}`}
              featured={index === 0}
              onClick={() =>
                openMarketPage(item, {
                  statusMessage:
                    t?.("status.market.open.ranking", "{{market}} aberto pelo ranking de lojas.", {
                      market: item.market,
                    }) || `${item.market} aberto pelo ranking de lojas.`,
                })
              }
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong>
                  #{index + 1} {item.market}
                </strong>
                <span style={getPermissionBadgeStyle(index === 0 ? "granted" : "prompt")}>
                  {(t?.("ranking.price.index", "indice") || "indice") + " "} {item.price_index}
                </span>
              </div>
              <div style={{ marginTop: 8, color: "#7c2d12" }}>
                {(t?.("ranking.price.average", "Media") || "Media") + ": "}
                {formatCurrency(item.average_price)}
              </div>
              <div style={{ marginTop: 6, color: "#7c2d12" }}>
                {(t?.("common.distance", "Distancia") || "Distancia") + ": "}
                {formatDistance(item.resolvedDistanceKm ?? item.distance_km)}
              </div>
              <div style={{ marginTop: 6, color: "#9a3412" }}>
                {t?.(
                  "ranking.price.meta",
                  "Produtos: {{count}} | contrib.: {{contributions}}",
                  {
                    count: item.tracked_products,
                    contributions: item.contributions,
                  }
                ) || ""}
              </div>
            </RankingCard>
          ))}
          {ranking.length === 0 && (
            <div style={{ color: "#64748b" }}>
              {t?.("ranking.price.empty", "Ainda nao ha ranking de preco disponivel.") ||
                "Ainda nao ha ranking de preco disponivel."}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <button onClick={() => loadMarketRankings(false)} style={ghostButtonStyle}>
          {t?.("ranking.hero.refresh", "Atualizar rankings") || "Atualizar rankings"}
        </button>
      </div>
    </div>
  );
}
